import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { 
  collection, 
  addDoc, 
  getDocs, 
  query, 
  orderBy,
  Timestamp,
  deleteDoc,
  doc,
  updateDoc
} from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import Sidebar from '../components/Sidebar/Sidebar';
import { useAdminRole } from '../hooks/useAdminRole';
import './FixedChargesPage.css';

const FixedChargesPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { role: userRole } = useAdminRole();
  const [fixedCharges, setFixedCharges] = useState([]);
  const [loadingCharges, setLoadingCharges] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);

  // Formulaire
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    amount: '',
    startMonth: ''
  });
  const [editingCharge, setEditingCharge] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  // Types prédéfinis
  const chargeTypes = ['Rent', 'Salary', 'Utility', 'Lease', 'Insurance', 'Other'];

  // Obtenir le mois actuel par défaut (format YYYY-MM)
  const getCurrentMonth = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  };

  // Vérifier l'état d'authentification
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
      if (!currentUser) {
        navigate('/admin');
      } else {
        loadFixedCharges();
        // Initialiser le mois de début par défaut
        setFormData(prev => ({ ...prev, startMonth: getCurrentMonth() }));
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Fermer le menu quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Vérifier si le clic est en dehors de tous les menus
      if (!event.target.closest('.menu-container')) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Charger les charges fixes depuis Firestore
  const loadFixedCharges = async () => {
    setLoadingCharges(true);
    try {
      const chargesRef = collection(db, 'fixedCharges');
      const q = query(chargesRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const chargesData = [];
      querySnapshot.forEach((doc) => {
        chargesData.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      setFixedCharges(chargesData);
    } catch (error) {
      console.error('Erreur lors du chargement des charges fixes:', error);
    } finally {
      setLoadingCharges(false);
    }
  };


  // Ajouter une nouvelle charge fixe
  const handleAddCharge = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.type.trim() || !formData.amount.trim() || !formData.startMonth.trim()) {
      alert('Veuillez remplir tous les champs');
      return;
    }

    const amount = parseFloat(formData.amount);
    if (isNaN(amount) || amount <= 0) {
      alert('Le montant doit être un nombre positif');
      return;
    }

    try {
      await addDoc(collection(db, 'fixedCharges'), {
        name: formData.name.trim(),
        type: formData.type.trim(),
        amount: amount,
        startMonth: formData.startMonth.trim(),
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });

      setFormData({ name: '', type: '', amount: '', startMonth: getCurrentMonth() });
      await loadFixedCharges();
    } catch (error) {
      console.error('Erreur lors de l\'ajout de la charge fixe:', error);
      alert('Erreur lors de l\'ajout de la charge fixe');
    }
  };

  // Modifier une charge fixe
  const handleEditCharge = (charge) => {
    setEditingCharge(charge);
    setFormData({
      name: charge.name,
      type: charge.type,
      amount: charge.amount.toString(),
      startMonth: charge.startMonth || charge.month || getCurrentMonth() // Support ancien format
    });
    setShowEditModal(true);
    setOpenMenuId(null);
  };

  // Sauvegarder les modifications
  const handleSaveEdit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.type.trim() || !formData.amount.trim() || !formData.startMonth.trim()) {
      alert('Veuillez remplir tous les champs');
      return;
    }

    const amount = parseFloat(formData.amount);
    if (isNaN(amount) || amount <= 0) {
      alert('Le montant doit être un nombre positif');
      return;
    }

    try {
      const chargeRef = doc(db, 'fixedCharges', editingCharge.id);
      await updateDoc(chargeRef, {
        name: formData.name.trim(),
        type: formData.type.trim(),
        amount: amount,
        startMonth: formData.startMonth.trim(),
        updatedAt: Timestamp.now()
      });

      setShowEditModal(false);
      setEditingCharge(null);
      setFormData({ name: '', type: '', amount: '', startMonth: getCurrentMonth() });
      await loadFixedCharges();
    } catch (error) {
      console.error('Erreur lors de la modification de la charge fixe:', error);
      alert('Erreur lors de la modification de la charge fixe');
    }
  };

  // Supprimer une charge fixe
  const handleDeleteCharge = async (chargeId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette charge fixe ?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'fixedCharges', chargeId));
      setOpenMenuId(null);
      await loadFixedCharges();
    } catch (error) {
      console.error('Erreur lors de la suppression de la charge fixe:', error);
      alert('Erreur lors de la suppression de la charge fixe');
    }
  };

  // Formater le montant
  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'TND',
      minimumFractionDigits: 2
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="fixed-charges-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Chargement...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="fixed-charges-page">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} userRole={userRole} />
      
      <div className="dashboard-content">
        <div className="dashboard-header">
          <button 
            className="sidebar-toggle-btn"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
          <h1>Charges Fixes</h1>
        </div>

        <div className="fixed-charges-content">
          {/* Formulaire d'ajout */}
          <div className="add-charge-form">
            <h2>Ajouter une charge fixe</h2>
            <form onSubmit={handleAddCharge}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Nom</label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ex: Loyer bureau"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="type">Type</label>
                  <select
                    id="type"
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    required
                  >
                    <option value="">Sélectionner un type</option>
                    {chargeTypes.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="amount">Montant (TND)</label>
                  <input
                    type="number"
                    id="amount"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="startMonth">Depuis le mois</label>
                  <input
                    type="month"
                    id="startMonth"
                    value={formData.startMonth}
                    onChange={(e) => setFormData({ ...formData, startMonth: e.target.value })}
                    required
                  />
                </div>

                <button type="submit" className="add-btn">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  Ajouter
                </button>
              </div>
            </form>
          </div>

          {/* Tableau des charges fixes */}
          <div className="charges-table-container">
            <h2>Liste des charges fixes</h2>
            {loadingCharges ? (
              <div className="loading-container">
                <div className="spinner"></div>
                <p>Chargement...</p>
              </div>
            ) : fixedCharges.length === 0 ? (
              <div className="empty-state">
                <p>Aucune charge fixe enregistrée</p>
              </div>
            ) : (
              <table className="charges-table">
                <thead>
                  <tr>
                    <th>Nom</th>
                    <th>Type</th>
                    <th>Montant</th>
                    <th>Depuis le mois</th>
                    <th>Date de création</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {fixedCharges.map((charge) => (
                    <tr key={charge.id}>
                      <td>{charge.name}</td>
                      <td>
                        <span className="type-badge">{charge.type}</span>
                      </td>
                      <td className="amount-cell">{formatAmount(charge.amount)}</td>
                      <td>
                        {(charge.startMonth || charge.month) ? (() => {
                          const monthValue = charge.startMonth || charge.month;
                          const [year, month] = monthValue.split('-');
                          const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
                          return `${monthNames[parseInt(month) - 1]} ${year}`;
                        })() : 'N/A'}
                      </td>
                      <td>
                        {charge.createdAt && charge.createdAt.toDate 
                          ? charge.createdAt.toDate().toLocaleDateString('fr-FR')
                          : 'N/A'}
                      </td>
                      <td>
                        <div className="menu-container">
                          <button
                            className="menu-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenMenuId(openMenuId === charge.id ? null : charge.id);
                            }}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="12" cy="12" r="1" />
                              <circle cx="12" cy="5" r="1" />
                              <circle cx="12" cy="19" r="1" />
                            </svg>
                          </button>
                          {openMenuId === charge.id && (
                            <div className="menu-dropdown">
                              <button
                                className="menu-item"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditCharge(charge);
                                }}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                </svg>
                                Modifier
                              </button>
                              <button
                                className="menu-item delete-item"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteCharge(charge.id);
                                }}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <polyline points="3 6 5 6 21 6" />
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                </svg>
                                Supprimer
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="3"><strong>Total</strong></td>
                    <td className="amount-cell">
                      <strong>{formatAmount(fixedCharges.reduce((sum, charge) => sum + charge.amount, 0))}</strong>
                    </td>
                    <td colSpan="2"></td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Modal de modification */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => {
          setShowEditModal(false);
          setEditingCharge(null);
          setFormData({ name: '', type: '', amount: '', startMonth: getCurrentMonth() });
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Modifier la charge fixe</h2>
              <button
                className="modal-close-btn"
                onClick={() => {
                  setShowEditModal(false);
                  setEditingCharge(null);
                  setFormData({ name: '', type: '', amount: '', startMonth: getCurrentMonth() });
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleSaveEdit} className="modal-form">
              <div className="form-group">
                <label htmlFor="edit-name">Nom</label>
                <input
                  type="text"
                  id="edit-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="edit-type">Type</label>
                <select
                  id="edit-type"
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  required
                >
                  <option value="">Sélectionner un type</option>
                  {chargeTypes.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="edit-amount">Montant (TND)</label>
                <input
                  type="number"
                  id="edit-amount"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  step="0.01"
                  min="0"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="edit-startMonth">Depuis le mois</label>
                <input
                  type="month"
                  id="edit-startMonth"
                  value={formData.startMonth}
                  onChange={(e) => setFormData({ ...formData, startMonth: e.target.value })}
                  required
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="modal-cancel-btn"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingCharge(null);
                    setFormData({ name: '', type: '', amount: '', startMonth: getCurrentMonth() });
                  }}
                >
                  Annuler
                </button>
                <button type="submit" className="modal-save-btn">
                  Enregistrer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FixedChargesPage;

